import { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAnalytics } from '../../hooks/useAnalytics';
import posthog from 'posthog-js';
import { CurrencyTooltip } from '../shared/CurrencyTooltip';
import { FieldTooltip } from '../shared/FieldTooltip';
import { FormulaDisclosure } from '../shared/FormulaDisclosure';
import { DiscountModifier } from '../shared/DiscountModifier';
import { RelatedKQLQueries } from '../shared/RelatedKQLQueries';
import { useCalculatorStore } from '../../store/calculatorStore';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ReferenceDot, ResponsiveContainer, Legend,
} from 'recharts';
import { useLogAnalyticsIngestionCalc } from '../../hooks/useCalculate';
import { Disclaimer } from '../shared/Disclaimer';
import { FeedbackModal } from '../shared/FeedbackModal';
import { ResourcesSection } from '../shared/ResourcesSection';
import { ShareButton } from '../shared/ShareButton';
import { MobileCostBar } from '../shared/MobileCostBar';
import type { LogAnalyticsIngestionRequest } from '../../api/types';
import { NumberInput } from '../ui/NumberInput';
import { Slider } from '../ui/Slider';
import { ScenarioPresets } from '../shared/ScenarioPresets';
import { Toggle } from '../ui/Toggle';

// Commitment tier flat monthly costs (GBP) and their break-even points vs PAYG
const TIER_CONFIG = [
  { gb: 100, monthly: 5430.60,  breakEven: 90,  color: '#0078d4' },
  { gb: 200, monthly: 10196.10, breakEven: 168, color: '#2899d4' },
  { gb: 300, monthly: 14961.90, breakEven: 246, color: '#50b4d4' },
  { gb: 500, monthly: 23966.40, breakEven: 394, color: '#78cfe0' },
] as const;

const PAYG_RATE = 2.167;  // £/GB — UK South PAYG Analytics Logs (from D1, verified Apr 2026)
const FREE_PER_DAY = 5;   // 5 GB/day free on PAYG

function paygMonthly(gbPerDay: number): number {
  return Math.max(0, gbPerDay - FREE_PER_DAY) * PAYG_RATE * 30;
}

function BreakEvenChart({ currentGB }: { currentGB: number }) {
  // Only show tiers up to 2× current slider value (minimum: show 100 GB tier)
  const maxTierGB = Math.max(100, currentGB * 2);
  const visibleTiers = TIER_CONFIG.filter((t) => t.gb <= maxTierGB);

  const data = useMemo(() => {
    const points = [];
    for (let gb = 0; gb <= 500; gb += 10) {
      const point: Record<string, number> = { gb, payg: Math.round(paygMonthly(gb)) };
      for (const tier of TIER_CONFIG) {
        point[`tier${tier.gb}`] = Math.round(tier.monthly);
      }
      points.push(point);
    }
    return points;
  }, []);

  const formatGBP = (v: number) => `£${v.toLocaleString()}`;

  return (
    <div className="bg-surface-container-high ghost-border rounded-lg p-5 mt-4">
      <h4 className="text-[11px] font-mono text-outline uppercase tracking-wider mb-1">
        Monthly cost by ingestion volume
      </h4>
      <p className="text-[10px] text-outline/60 mb-4">
        Break-even points show where commitment tiers become cost-effective
      </p>
      <ResponsiveContainer width="100%" height={260}>
        <LineChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--outline-variant)" />
          <XAxis
            dataKey="gb"
            tick={{ fill: 'var(--text-tertiary)', fontSize: 10 }}
            tickFormatter={(v: number) => `${v}GB`}
            interval={4}
          />
          <YAxis
            tick={{ fill: 'var(--text-tertiary)', fontSize: 10 }}
            tickFormatter={(v: number) => `£${(v / 1000).toFixed(0)}k`}
            width={42}
          />
          <Tooltip
            contentStyle={{ background: 'var(--surface-container)', border: '1px solid var(--outline-variant)', borderRadius: 4 }}
            labelStyle={{ color: 'var(--text-tertiary)', fontSize: 11 }}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            formatter={(value: any, name: any) => {
              const label = name === 'payg' ? 'PAYG' : `${String(name).replace('tier', '')} GB/day tier`;
              return [formatGBP(Number(value)), label];
            }}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            labelFormatter={(v: any) => `${v} GB/day`}
          />
          <Legend
            wrapperStyle={{ fontSize: 10, color: 'var(--text-tertiary)', paddingTop: 8 }}
            formatter={(value: string) => value === 'payg' ? 'PAYG' : `${value.replace('tier', '')} GB/day tier`}
          />
          {/* PAYG line */}
          <Line
            type="monotone" dataKey="payg" stroke="var(--text-secondary)"
            strokeWidth={2} dot={false} name="payg"
          />
          {/* Tier lines — only show relevant ones */}
          {visibleTiers.map((tier) => (
            <Line
              key={tier.gb}
              type="monotone"
              dataKey={`tier${tier.gb}`}
              stroke={tier.color}
              strokeWidth={1.5}
              strokeDasharray="4 2"
              dot={false}
              name={`tier${tier.gb}`}
            />
          ))}
          {/* Break-even dots */}
          {visibleTiers.map((tier) => (
            <ReferenceDot
              key={`dot-${tier.gb}`}
              x={tier.breakEven}
              y={Math.round(paygMonthly(tier.breakEven))}
              r={5}
              fill="#fbbf24"
              stroke="var(--surface-container-low)"
              strokeWidth={2}
              label={{
                value: `~${tier.breakEven}GB`,
                position: 'top',
                fill: '#fbbf24',
                fontSize: 9,
              }}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
      <p className="text-[10px] text-outline/50 mt-2 italic">
        Amber dots = break-even points. Dashed lines = commitment tiers shown up to 2× your current input.
      </p>
    </div>
  );
}

const COMMITMENT_TIERS = [100, 200, 300, 400, 500] as const;

type LogType = 'analytics' | 'basic' | 'auxiliary';
type DataLakeScenario = 'analytics_only' | 'analytics_data_lake' | 'data_lake_only';

const LOG_TYPE_LABELS: Record<LogType, { label: string; sub: string; price: string }> = {
  analytics: { label: 'Analytics', sub: 'Full KQL, alerts, dashboards', price: '£2.167/GB' },
  basic:      { label: 'Basic Logs', sub: 'Reduced features, 8-day retention', price: '£0.46/GB' },
  auxiliary:  { label: 'Auxiliary Logs', sub: 'Query-only, cheapest tier', price: '£0.04/GB' },
};

const DATA_LAKE_RETENTION_RATE = 0.02; // approximate £/GB/month for Data Lake retention
const DATA_LAKE_COMPUTE_RATE   = 6.40; // approximate £/TB scanned
const ANALYTICS_FREE_RETENTION_MONTHS = 3;


interface LogAnalyticsIngestionCalcProps {
  initialDailyIngestionGB?: number;
}

export default function LogAnalyticsIngestionCalc({ initialDailyIngestionGB }: LogAnalyticsIngestionCalcProps) {
  const [inputs, setInputs] = useState<LogAnalyticsIngestionRequest>({
    dailyIngestionGB: initialDailyIngestionGB ?? 10,
    logType: 'analytics',
    sentinelEnabled: false,
    planType: 'payg',
    commitmentTierGB: null,
    daysPerMonth: 30,
  });

  const [dataLakeScenario, setDataLakeScenario] = useState<DataLakeScenario>('analytics_only');
  const [activeRetentionMonths, setActiveRetentionMonths] = useState(3);
  const [totalRetentionMonths, setTotalRetentionMonths] = useState(12);
  const [dataLakeTBScanned, setDataLakeTBScanned] = useState(0);
  const [alertsOpen, setAlertsOpen] = useState(false);
  const [alertInputs, setAlertInputs] = useState({ metricAlertRules: 0, logAlertRules: 0, smsNotificationsPerMonth: 0 });

  const { discount, setDiscount } = useCalculatorStore();

  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [kqlRates, setKqlRates] = useState<{ analyticsLogsGBP: number; sentinelGBP: number; lastUpdated: string } | null>(null);
  const [kqlVerifyExpanded, setKqlVerifyExpanded] = useState(true);
  const [copiedKql, setCopiedKql] = useState(false);

  useEffect(() => {
    fetch('/api/kql-rates')
      .then((res) => res.json())
      .then((data) => setKqlRates(data))
      .catch((err) => {
        console.error('Failed to fetch KQL rates:', err);
        setKqlRates({ analyticsLogsGBP: 2.1339, sentinelGBP: 2.297, lastUpdated: new Date().toISOString() });
      });
  }, []);

  const set = (partial: Partial<LogAnalyticsIngestionRequest>) =>
    setInputs((prev) => ({ ...prev, ...partial }));

  const queryInputs = alertsOpen
    ? { ...inputs, alerts: alertInputs }
    : inputs;

  const { data, isLoading, isError } = useLogAnalyticsIngestionCalc(queryInputs);
  const { trackCalculatorUsed } = useAnalytics();
  useEffect(() => {
    if (data?.monthly_estimate) {
      trackCalculatorUsed({ calculator: 'log-analytics', result_gbp: data.monthly_estimate, tier: inputs.commitmentTierGB ? `${inputs.commitmentTierGB}GB` : 'payg' })
    }
  }, [data?.monthly_estimate])
  const monthlyGB = inputs.dailyIngestionGB * (inputs.daysPerMonth ?? 30);

  const generateVerificationKql = () => {
    if (!kqlRates) return '';

    const rate = inputs.logType === 'analytics' ? kqlRates.analyticsLogsGBP : (inputs.logType === 'basic' ? 0.4631 : 0.0445);
    const baseQuery = `// Verify Log Analytics ingestion cost
// Daily target: ${inputs.dailyIngestionGB} GB, Rate: £${rate.toFixed(4)}/GB
union withsource=_Table kind=inner
  (print table_name="Analytics Logs", bytes=_IsBillable * _BilledSize)
${inputs.sentinelEnabled ? `  , (print table_name="Sentinel", bytes=_IsBillable * _BilledSize)` : ''}
| where _IsBillable == true
| extend gb = bytes / (1024 * 1024 * 1024)
| summarize total_gb = sum(gb) by table_name
| extend daily_gb = total_gb / 1
| extend cost_gbp = daily_gb * ${rate.toFixed(4)}
${inputs.sentinelEnabled ? '| extend cost_with_sentinel_gbp = cost_gbp + (daily_gb * ' + kqlRates.sentinelGBP.toFixed(4) + ')' : ''}`;

    return baseQuery;
  };

  const copyKqlToClipboard = async (kql: string) => {
    try {
      await navigator.clipboard.writeText(kql);
      setCopiedKql(true);
      setTimeout(() => setCopiedKql(false), 2000);
      if (import.meta.env.PROD) {
        posthog.capture('kql_verification_copied', { log_type: inputs.logType });
      }
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const openInAzurePortal = (kql: string) => {
    const encodedQuery = btoa(kql);
    const portalUrl = `https://portal.azure.com/#view/Microsoft_Azure_Monitoring_Logs/LogsBlade/resourceId/%2Fsubscriptions%2F{subscription-id}%2FresourceGroups%2F{resource-group}%2Fproviders%2Fmicrosoft.operationalinsights%2Fworkspaces%2F{workspace-name}/source/LogsBlade.AnalyticsShareLinkToQuery/q/${encodedQuery}`;
    window.open(portalUrl, '_blank');
    if (import.meta.env.PROD) {
      posthog.capture('kql_verification_portal_opened', { log_type: inputs.logType });
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-[1fr_380px] gap-8 w-full pb-14 md:pb-0">
      {/* ── Left: Controls ── */}
      <div className="flex flex-col gap-6">
        <div className={`bg-surface-container-high micro-gradient ghost-border rounded-lg p-6 transition-opacity ${isLoading ? 'opacity-70' : ''}`}>
          <h2 className="font-headline font-bold text-lg flex items-center gap-2 mb-6">
            <span className="material-symbols-outlined text-primary" style={{ fontVariationSettings: "'FILL' 1" }}>cloud_upload</span>
            Ingestion Configuration
          </h2>

          <ScenarioPresets presets={[
            { label: 'Small SOC', description: '~10 GB/day, PAYG', apply: () => set({ dailyIngestionGB: 10, planType: 'payg', commitmentTierGB: null }) },
            { label: 'Mid enterprise', description: '~50 GB/day, PAYG', apply: () => set({ dailyIngestionGB: 50, planType: 'payg', commitmentTierGB: null }) },
            { label: 'High volume', description: '~200 GB/day, commitment tier', apply: () => set({ dailyIngestionGB: 200, planType: 'commitment', commitmentTierGB: 200 }) },
          ]} />

          {/* Daily ingestion slider */}
          <div className="flex flex-col gap-4 mb-6">
            <div className="flex justify-between items-end">
              <label className="text-[11px] font-mono text-outline uppercase tracking-wider">Daily Ingestion Volume</label>
              <span className="text-xl font-bold font-mono text-primary">{inputs.dailyIngestionGB} GB/day</span>
            </div>
            <Slider
              value={inputs.dailyIngestionGB}
              onChange={(value) => set({ dailyIngestionGB: value })}
              min={0}
              max={500}
              step={1}
              label="Daily Ingestion Volume"
              formatValue={(n) => `${n} GB/day`}
            />
            <div className="flex justify-between text-[10px] text-outline font-mono">
              <span>0 GB</span>
              <span className="text-outline/60">{monthlyGB.toLocaleString()} GB/month</span>
              <span>500 GB</span>
            </div>
          </div>

          {/* Log type */}
          <div className="flex flex-col gap-2 mb-6">
            <label className="text-[11px] font-mono text-outline uppercase tracking-wider">Log Type <FieldTooltip content="Analytics: full KQL query support. Basic: limited queries, cheaper ingestion. Auxiliary: cheapest, minimal querying." /></label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {(Object.keys(LOG_TYPE_LABELS) as LogType[]).map((t) => {
                const info = LOG_TYPE_LABELS[t];
                const active = inputs.logType === t;
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => set({ logType: t, planType: t !== 'analytics' ? 'payg' : inputs.planType, commitmentTierGB: t !== 'analytics' ? null : inputs.commitmentTierGB })}
                    className={`p-3 text-left transition-all border ${active ? 'bg-primary-container/10 border-primary-container' : 'bg-surface-container-lowest border-outline-variant hover:bg-surface-container'}`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <div className={`w-3 h-3 rounded-full shrink-0 ${active ? 'bg-primary shadow-[0_0_8px_rgba(0,120,212,0.6)]' : 'border border-outline'}`} />
                      <span className={`text-sm font-bold ${active ? 'text-on-surface' : 'text-outline'}`}>{info.label}</span>
                    </div>
                    <p className="text-[10px] text-outline ml-5">{info.sub}</p>
                    <p className={`text-[11px] font-mono font-bold ml-5 mt-1 ${active ? 'text-primary' : 'text-outline/70'}`}>{info.price}</p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Sentinel toggle */}
          <div className="flex items-center justify-between p-4 bg-surface-container-lowest/50 border border-outline-variant/50 rounded-sm mb-6">
            <div className="flex items-center gap-3">
              <span className="material-symbols-outlined text-[#e8b000]" style={{ fontVariationSettings: "'FILL' 1" }}>security</span>
              <div>
                <p className="text-sm font-bold">Microsoft Sentinel</p>
                <p className="text-[10px] text-outline">SIEM analytics charged separately on all ingested data</p>
              </div>
            </div>
            <Toggle checked={inputs.sentinelEnabled} onChange={(checked) => set({ sentinelEnabled: checked })} label="Enable Sentinel" />
          </div>

          {/* Plan type — analytics only */}
          {inputs.logType === 'analytics' && (
            <div className="flex flex-col gap-2 mb-6">
              <label className="text-[11px] font-mono text-outline uppercase tracking-wider">Billing Plan <FieldTooltip content="Commitment Tier locks in a daily GB volume at a ~25–30% discount vs Pay-As-You-Go. Worth it above ~100 GB/day." /></label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {(['payg', 'commitment'] as const).map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => set({ planType: p, commitmentTierGB: p === 'payg' ? null : inputs.commitmentTierGB ?? 100 })}
                    className={`p-3 transition-all border text-sm font-bold ${inputs.planType === p ? 'bg-primary-container/10 border-primary-container text-on-surface' : 'bg-surface-container-lowest border-outline-variant text-outline hover:bg-surface-container'}`}
                  >
                    {p === 'payg' ? 'Pay-As-You-Go' : 'Commitment Tier'}
                  </button>
                ))}
              </div>

              {inputs.planType === 'commitment' && (
                <div className="mt-3 flex flex-col gap-2">
                  <label className="text-[11px] font-mono text-outline uppercase tracking-wider">Commitment Tier (GB/day) <FieldTooltip content="You commit to ingest at least this many GB daily. If you ingest less, you still pay the committed rate." /></label>
                  <div className="flex flex-wrap gap-2">
                    {COMMITMENT_TIERS.map((gb) => (
                      <button
                        key={gb}
                        type="button"
                        onClick={() => set({ commitmentTierGB: gb })}
                        className={`px-4 py-2 text-sm font-mono font-bold border transition-all ${inputs.commitmentTierGB === gb ? 'bg-primary/20 border-primary text-primary' : 'bg-surface-container-lowest border-outline-variant text-outline hover:bg-surface-container'}`}
                      >
                        {gb} GB
                      </button>
                    ))}
                  </div>
                  <p className="text-[10px] text-outline italic">Commitment tiers start at 100 GB/day. Fixed daily rate regardless of actual ingestion.</p>
                </div>
              )}
            </div>
          )}

          {/* Days per month */}
          <div className="flex flex-col gap-2">
            <div className="flex justify-between items-end">
              <label className="text-[11px] font-mono text-outline uppercase tracking-wider">Days / Month</label>
              <span className="text-sm font-mono font-bold">{inputs.daysPerMonth ?? 30}</span>
            </div>
            <Slider
              value={inputs.daysPerMonth ?? 30}
              onChange={(value) => set({ daysPerMonth: value })}
              min={28}
              max={31}
              step={1}
              label="Days / Month"
              formatValue={(n) => `${n}`}
            />
          </div>
        </div>

        {/* Data Lake Scenario Selector */}
        <div className="bg-surface-container-high micro-gradient ghost-border rounded-lg p-6">
          <h2 className="font-headline font-bold text-lg flex items-center gap-2 mb-4">
            <span className="material-symbols-outlined text-primary" style={{ fontVariationSettings: "'FILL' 1" }}>water</span>
            Sentinel Data Lake
          </h2>
          <p className="text-[11px] text-outline mb-4">Select a storage scenario to compare Analytics vs Data Lake costs.</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-4">
            {([
              { id: 'analytics_only', label: 'Analytics only', sub: 'Existing behaviour — no change' },
              { id: 'analytics_data_lake', label: 'Analytics + Data Lake', sub: 'Auto-mirror to Data Lake at no extra ingestion cost' },
              { id: 'data_lake_only', label: 'Data Lake only', sub: 'Coming soon' },
            ] as { id: DataLakeScenario; label: string; sub: string }[]).map((s) => (
              <button
                key={s.id}
                type="button"
                disabled={s.id === 'data_lake_only'}
                onClick={() => setDataLakeScenario(s.id)}
                className={`p-3 text-left transition-all border ${
                  dataLakeScenario === s.id
                    ? 'bg-primary-container/10 border-primary-container'
                    : 'bg-surface-container-lowest border-outline-variant hover:bg-surface-container'
                } ${s.id === 'data_lake_only' ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
              >
                <span className={`text-sm font-bold block ${dataLakeScenario === s.id ? 'text-on-surface' : 'text-outline'}`}>{s.label}</span>
                <span className="text-[10px] text-outline/70 mt-1 block">{s.sub}</span>
              </button>
            ))}
          </div>

          {/* Scenario B — Analytics + Data Lake inputs */}
          {dataLakeScenario === 'analytics_data_lake' && (
            <div className="flex flex-col gap-4 mt-4 border-t border-outline-variant/40 pt-4">
              <p className="text-[11px] text-outline italic">
                First {ANALYTICS_FREE_RETENTION_MONTHS} months of Analytics retention are FREE. Data beyond that is mirrored to Data Lake at ~£{DATA_LAKE_RETENTION_RATE}/GB/month.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex flex-col gap-1">
                  <NumberInput
                    label="Active querying period (months)"
                    value={activeRetentionMonths}
                    onChange={(value) => setActiveRetentionMonths(Math.max(1, value || 1))}
                    min={1}
                    max={24}
                    step={1}
                    inputMode="numeric"
                  />
                  <span className="text-[10px] text-outline/60">Data stays in Analytics (full cost)</span>
                </div>
                <div className="flex flex-col gap-1">
                  <NumberInput
                    label="Total retention (months)"
                    value={totalRetentionMonths}
                    onChange={(value) => setTotalRetentionMonths(Math.max(activeRetentionMonths, value || activeRetentionMonths))}
                    min={activeRetentionMonths}
                    max={84}
                    step={1}
                    inputMode="numeric"
                  />
                  <span className="text-[10px] text-outline/60">Beyond active period → Data Lake</span>
                </div>
              </div>
              <div className="flex flex-col gap-1">
                <NumberInput
                  label="Data Lake compute queries (TB scanned/month, optional)"
                  value={dataLakeTBScanned}
                  onChange={(value) => setDataLakeTBScanned(Math.max(0, value || 0))}
                  min={0}
                  step={0.1}
                  inputMode="decimal"
                />
                <span className="text-[10px] text-outline/60">~£{DATA_LAKE_COMPUTE_RATE}/TB scanned</span>
              </div>
              {/* Break-even callout */}
              {(() => {
                // Break-even: at what daily GB does adding Data Lake tier become cheaper than keeping all in Analytics?
                // Simplistic: if archive months > free months, Data Lake saves (archiveMonths - freeMonths) × monthlyGB × (analyticsRate - dataLakeRate)
                const archiveMonths = Math.max(0, totalRetentionMonths - activeRetentionMonths);
                const monthlyGB = inputs.dailyIngestionGB * (inputs.daysPerMonth ?? 30);
                const analyticsCostForArchive = monthlyGB * 0.012 * archiveMonths; // ~£0.012/GB/month for analytics archive
                const dataLakeCostForArchive = monthlyGB * DATA_LAKE_RETENTION_RATE * archiveMonths;
                const computeCost = dataLakeTBScanned * DATA_LAKE_COMPUTE_RATE;
                const netSaving = analyticsCostForArchive - dataLakeCostForArchive - computeCost;
                if (archiveMonths <= 0) return null;
                return (
                  <div className={`p-3 rounded-sm border flex items-start gap-2 ${netSaving > 0 ? 'bg-[#107c10]/10 border-[#107c10]/40' : 'bg-surface-container-lowest border-outline-variant'}`}>
                    <span className={`material-symbols-outlined text-[14px] shrink-0 mt-0.5 ${netSaving > 0 ? 'text-[#107c10]' : 'text-outline'}`}>lightbulb</span>
                    <p className="text-[11px] leading-snug text-on-surface">
                      {netSaving > 0
                        ? `At ${inputs.dailyIngestionGB} GB/day with ${archiveMonths} months in Data Lake, estimated saving vs keeping all in Analytics: ~£${netSaving.toFixed(2)}/month.`
                        : `At this volume and compute usage, keeping data in Analytics may be more cost-effective. Try reducing query volume or increasing archive duration.`}
                    </p>
                  </div>
                );
              })()}
            </div>
          )}
        </div>

        {/* ── Monitor Alerts (Sprint 12 extension) ── */}
        {/* Prices from D1 (Azure Monitor, uksouth, 2026-04-14): metric £0.0752/rule/mo, log £1.1286/rule/mo, SMS UK £0.0271/notification */}
        <div className="bg-surface-container rounded-lg border border-outline-variant overflow-hidden">
          <button
            className="w-full flex items-center justify-between px-4 py-3 text-left"
            onClick={() => setAlertsOpen((v) => !v)}
          >
            <span className="flex items-center gap-2 text-[13px] font-semibold" style={{ color: 'var(--text-secondary)' }}>
              <span className="material-symbols-outlined text-[18px]" style={{ color: '#0078d4' }}>notifications</span>
              Monitor Alerts
              <span className="text-[11px] font-normal ml-1" style={{ color: 'var(--text-tertiary)' }}>(optional — add to estimate)</span>
            </span>
            <span className="material-symbols-outlined text-[18px]" style={{ color: 'var(--text-tertiary)' }}>
              {alertsOpen ? 'expand_less' : 'expand_more'}
            </span>
          </button>

          {alertsOpen && (
            <div className="px-4 pb-4 flex flex-col gap-3 border-t border-outline-variant" style={{ borderColor: 'var(--outline-variant)' }}>
              <div className="mt-3">
                <label className="block text-[12px] mb-1" style={{ color: 'var(--text-secondary)' }}>
                  Metric alert rules
                  <span className="ml-1 text-[11px]" style={{ color: 'var(--text-tertiary)' }}>£0.0752/rule/month</span>
                </label>
                <input
                  type="number"
                  min={0}
                  value={alertInputs.metricAlertRules}
                  onChange={(e) => setAlertInputs((a) => ({ ...a, metricAlertRules: Number(e.target.value) }))}
                  className="w-full px-3 py-2 rounded text-[13px]"
                  style={{ backgroundColor: 'var(--surface-container-low)', border: '1px solid var(--outline-variant)', color: 'var(--text-primary)' }}
                />
              </div>
              <div>
                <label className="block text-[12px] mb-1" style={{ color: 'var(--text-secondary)' }}>
                  Log search alert rules
                  <span className="ml-1 text-[11px]" style={{ color: 'var(--text-tertiary)' }}>£1.1286/rule/month (5-min frequency)</span>
                </label>
                <input
                  type="number"
                  min={0}
                  value={alertInputs.logAlertRules}
                  onChange={(e) => setAlertInputs((a) => ({ ...a, logAlertRules: Number(e.target.value) }))}
                  className="w-full px-3 py-2 rounded text-[13px]"
                  style={{ backgroundColor: 'var(--surface-container-low)', border: '1px solid var(--outline-variant)', color: 'var(--text-primary)' }}
                />
              </div>
              <div
                className="rounded px-3 py-2 text-[11px]"
                style={{ backgroundColor: 'rgba(16,124,16,0.06)', border: '1px solid rgba(16,124,16,0.2)', color: '#107c10' }}
              >
                Activity log alerts are always free — no charge per rule.
              </div>
              <div>
                <label className="block text-[12px] mb-1" style={{ color: 'var(--text-secondary)' }}>
                  SMS notifications/month (UK numbers)
                  <span className="ml-1 text-[11px]" style={{ color: 'var(--text-tertiary)' }}>£0.0271/notification · email is free</span>
                </label>
                <input
                  type="number"
                  min={0}
                  value={alertInputs.smsNotificationsPerMonth}
                  onChange={(e) => setAlertInputs((a) => ({ ...a, smsNotificationsPerMonth: Number(e.target.value) }))}
                  className="w-full px-3 py-2 rounded text-[13px]"
                  style={{ backgroundColor: 'var(--surface-container-low)', border: '1px solid var(--outline-variant)', color: 'var(--text-primary)' }}
                />
              </div>
              {data?.alerts && (
                <div
                  className="rounded px-3 py-2 text-[12px] font-mono"
                  style={{ backgroundColor: 'var(--surface-container-low)', border: '1px solid var(--outline-variant)', color: 'var(--text-secondary)' }}
                >
                  <p>Metric alerts: £{data.alerts.metricCost.toFixed(2)}</p>
                  <p>Log alerts: £{data.alerts.logCost.toFixed(2)}</p>
                  {data.alerts.smsCost > 0 && <p>SMS: £{data.alerts.smsCost.toFixed(2)}</p>}
                  <p className="font-semibold mt-1" style={{ color: 'var(--text-primary)' }}>
                    Alerts total: £{data.alerts.total.toFixed(2)}/month
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Right: Results ── */}
      <div className="flex flex-col gap-6">
        <div className="sticky top-[72px] flex flex-col gap-6">
          {/* Monthly estimate */}
          <div id="la-results" className="bg-surface-container-high micro-gradient border-l-2 border-primary rounded-lg overflow-hidden">
            <div className="p-6">
              <h3 className="font-mono text-[11px] text-outline uppercase tracking-widest mb-2">Estimated Monthly Cost (PAYG)</h3>
              <div className="flex items-baseline gap-2">
                <span className="text-5xl font-bold font-headline">
                  {isLoading ? '...' : isError ? 'Error' : `£${data?.monthly_estimate.toFixed(2)}`}
                </span>
                {!isLoading && !isError && data && <CurrencyTooltip />}
                <span className="text-outline text-sm">/mo</span>
                <button
                  type="button"
                  onClick={() => setFeedbackOpen(true)}
                  title="Report an issue with this estimate"
                  className="ml-1 transition-colors"
                  style={{ color: 'var(--text-tertiary)' }}
                >
                  <span className="material-symbols-outlined text-[18px]">help</span>
                </button>
              </div>
            </div>
            <div className="bg-surface-container-lowest/50 p-6 space-y-4">
              <h4 className="text-xs font-bold uppercase tracking-wider text-on-surface-variant flex items-center gap-2">
                <span className="material-symbols-outlined text-sm">list_alt</span>
                Cost Breakdown
              </h4>
              <ul className="space-y-3">
                {data?.breakdown.map((item, idx) => (
                  <li key={idx} className="flex flex-col gap-0.5">
                    <div className="flex justify-between items-center font-mono text-xs">
                      <span className="text-outline">{item.label}</span>
                      <span className={`font-bold ${item.isFree ? 'text-[#107c10]' : ''}`}>
                        {item.isFree ? 'FREE' : `£${item.value.toFixed(2)}`}
                      </span>
                    </div>
                    {item.note && <p className="text-[10px] text-outline/60 italic">{item.note}</p>}
                  </li>
                ))}
                {!data && !isLoading && <p className="text-[10px] text-outline text-center py-4">Adjust inputs to see pricing</p>}
                {data?.alerts && (
                  <li className="py-2 border-t border-outline-variant flex justify-between items-center font-mono text-[12px]">
                    <span style={{ color: 'var(--text-secondary)' }}>
                      Monitor Alerts ({alertInputs.metricAlertRules} metric + {alertInputs.logAlertRules} log rules{alertInputs.smsNotificationsPerMonth > 0 ? ` + ${alertInputs.smsNotificationsPerMonth} SMS` : ''})
                    </span>
                    <span style={{ color: 'var(--text-primary)' }}>£{data.alerts.total.toFixed(2)}</span>
                  </li>
                )}
                {data && (
                  <li className="pt-3 border-t border-outline-variant flex justify-between items-center font-mono text-sm">
                    <span className="font-bold">Total</span>
                    <span className="font-bold text-primary">£{data.monthly_estimate.toFixed(2)}</span>
                  </li>
                )}
              </ul>
            </div>

            {data && (
              <div className="px-6 pb-2">
                <FormulaDisclosure
                  lines={[
                    ...data.breakdown.map((item) => `${item.label}: ${item.isFree ? 'FREE' : `£${item.value.toFixed(4)}`}`),
                    `Log type: ${inputs.logType} · Plan: ${inputs.planType} · ${inputs.dailyIngestionGB}GB/day · Region: UK South`,
                  ]}
                  fetchedAt={data.prices_last_updated}
                />
              </div>
            )}
            {/* Sentinel warning */}
            {inputs.sentinelEnabled && data?.warnings && data.warnings.length > 0 && (
              <div className="border-t border-[#e8b000]/30 bg-[#e8b000]/5 p-4">
                {data.warnings.map((w, i) => (
                  <p key={i} className="text-[10px] text-[#e8b000] leading-relaxed flex items-start gap-2">
                    <span className="material-symbols-outlined text-[14px] shrink-0 mt-0.5">warning</span>
                    {w}
                  </p>
                ))}
              </div>
            )}
          </div>

          {/* Verify in your workspace panel */}
          {data && (
            <div className="bg-surface-container-high ghost-border rounded-lg overflow-hidden">
              <button
                onClick={() => setKqlVerifyExpanded(!kqlVerifyExpanded)}
                className="w-full flex items-center justify-between p-5 hover:bg-surface-container-highest/50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="material-symbols-outlined text-primary" style={{ fontVariationSettings: "'FILL' 1" }}>terminal</span>
                  <h3 className="text-sm font-bold">Verify in your workspace</h3>
                </div>
                <span className="material-symbols-outlined text-outline transition-transform" style={{ transform: kqlVerifyExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}>expand_more</span>
              </button>

              {kqlVerifyExpanded && (
                <>
                  <div className="border-t border-outline-variant/30 px-5 py-4">
                    <p className="text-[11px] text-outline-variant leading-relaxed mb-4">
                      Run this KQL query against your Log Analytics workspace to validate ingestion volume and cost using the same rate as this calculator.
                    </p>

                    {/* KQL Query Block */}
                    <div className="bg-surface-container-lowest/80 border border-outline-variant/40 rounded-sm p-4 mb-4 font-mono text-[10px] overflow-x-auto max-h-48 overflow-y-auto">
                      <pre className="whitespace-pre-wrap break-words text-outline">{generateVerificationKql()}</pre>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-2 mb-4">
                      <button
                        onClick={() => copyKqlToClipboard(generateVerificationKql())}
                        className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded bg-primary-container/20 hover:bg-primary-container/30 text-primary border border-primary/30 text-xs font-semibold transition-colors"
                      >
                        <span className="material-symbols-outlined text-sm">content_copy</span>
                        {copiedKql ? 'Copied ✓' : 'Copy KQL'}
                      </button>
                      <button
                        onClick={() => openInAzurePortal(generateVerificationKql())}
                        className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded text-white text-xs font-semibold transition-colors"
                        style={{ backgroundColor: '#0078d4' }}
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#106ebe')}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#0078d4')}
                      >
                        <span className="material-symbols-outlined text-sm">arrow_outward</span>
                        Open in Portal
                      </button>
                    </div>

                    {/* Metadata */}
                    <div className="text-[10px] text-outline-variant flex items-start gap-2 p-3 bg-surface-container-lowest/50 rounded-sm">
                      <span className="material-symbols-outlined text-xs shrink-0 mt-0.5">info</span>
                      <div>
                        <p>Rate: £{inputs.logType === 'analytics' ? (kqlRates?.analyticsLogsGBP ?? 2.1339) : (inputs.logType === 'basic' ? 0.4631 : 0.0445)}/GB (UK South)</p>
                        {kqlRates && <p className="text-outline/50">Last updated: {new Date(kqlRates.lastUpdated).toLocaleDateString()}</p>}
                        <p className="mt-1 italic text-outline/60">AzureCalc.uk never connects to your Azure subscription.</p>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Tier comparison — analytics only */}
          {data?.tier_comparison && data.tier_comparison.length > 1 && (
            <div className="bg-surface-container-high ghost-border rounded-lg p-5">
              <h4 className="text-[11px] font-mono text-outline uppercase tracking-wider mb-1">Commitment Tier Comparison</h4>
              <p className="text-[10px] text-outline/60 mb-4">At {inputs.dailyIngestionGB} GB/day ingestion</p>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-outline-variant/40">
                      <th className="text-left text-[10px] font-mono text-outline pb-2">Tier</th>
                      <th className="text-right text-[10px] font-mono text-outline pb-2">£/month</th>
                      <th className="text-right text-[10px] font-mono text-outline pb-2">£/GB eff.</th>
                      <th className="text-right text-[10px] font-mono text-outline pb-2">Break-even</th>
                    </tr>
                  </thead>
                  <tbody className="space-y-1">
                    {data.tier_comparison.map((row, idx) => (
                      <tr
                        key={idx}
                        className={`border-b border-outline-variant/20 transition-colors ${row.is_current_selection ? 'bg-primary-container/10' : ''}`}
                      >
                        <td className={`py-2 pr-2 ${row.is_current_selection ? 'font-bold text-primary' : 'text-on-surface-variant'}`}>
                          {row.tier}
                          {row.is_cheapest && <span className="ml-1 text-[9px] text-[#107c10] font-bold uppercase">Best</span>}
                        </td>
                        <td className="py-2 text-right font-mono">£{row.total.toFixed(0)}</td>
                        <td className="py-2 text-right font-mono">£{row.effective_price_per_gb.toFixed(3)}</td>
                        <td className="py-2 text-right font-mono text-outline">
                          {row.break_even_daily_gb !== undefined ? `${row.break_even_daily_gb} GB/d` : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {data.commitment_recommendation && (
                <div className="mt-4 p-3 bg-surface-container-lowest/60 border border-outline-variant/50 rounded-sm">
                  <p className="text-[11px] text-outline flex items-start gap-2">
                    <span className="material-symbols-outlined text-primary text-[14px] shrink-0 mt-0.5">lightbulb</span>
                    {data.commitment_recommendation}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Break-even chart — analytics only */}
          {inputs.logType === 'analytics' && (
            <BreakEvenChart currentGB={inputs.dailyIngestionGB} />
          )}

          {/* KQL pairing — verify estimate in workspace */}
          {data && (
            <div className="bg-surface-container-high rounded-lg p-5 border border-outline-variant/30">
              <RelatedKQLQueries
                paygRate={PAYG_RATE}
                sentinelEnabled={inputs.sentinelEnabled}
              />
            </div>
          )}

          {/* Deep-link to KQL builder with effective rate */}
          {data && data.monthly_estimate > 0 && (() => {
            const effectiveRate = data.tier_comparison?.find((r) => r.is_current_selection)?.effective_price_per_gb ?? PAYG_RATE;
            const kqlUrl = `/kql-builder?rate=${effectiveRate.toFixed(3)}`;
            return (
              <div className="bg-surface-container-high ghost-border rounded-lg p-5 flex flex-col gap-3">
                <div className="flex items-start gap-3">
                  <span className="material-symbols-outlined text-primary text-[20px] shrink-0 mt-0.5" style={{ fontVariationSettings: "'FILL' 1" }}>terminal</span>
                  <div className="min-w-0">
                    <h3 className="text-[13px] font-bold text-on-surface mb-1">Build a KQL cost query for this workspace</h3>
                    <p className="text-[12px] text-on-surface-variant leading-relaxed">
                      Model the cost of specific KQL queries based on your ingestion volume
                      ({inputs.dailyIngestionGB} GB/day) and effective rate (£{effectiveRate.toFixed(3)}/GB).
                    </p>
                  </div>
                </div>
                <Link
                  to={kqlUrl}
                  onClick={() => {
                    if (import.meta.env.PROD) {
                      posthog.capture('kql_pairing_clicked', {
                        rate_per_gb: effectiveRate,
                        daily_ingestion_gb: inputs.dailyIngestionGB,
                      });
                    }
                  }}
                  className="self-start inline-flex items-center gap-2 px-4 py-2 rounded text-[12px] font-semibold transition-colors text-white"
                  style={{ backgroundColor: '#0078d4' }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#106ebe')}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#0078d4')}
                >
                  Open KQL Builder with my numbers
                  <span className="material-symbols-outlined text-[16px]">arrow_forward</span>
                </Link>
              </div>
            );
          })()}

          {/* Discount modifier */}
          {data && (
            <DiscountModifier
              discount={discount}
              onDiscountChange={setDiscount}
              paygTotal={data.monthly_estimate}
            />
          )}

          <div className="bg-surface-container-lowest/50 border border-outline-variant p-4 text-[10px] text-outline leading-relaxed">
            <Disclaimer />
          </div>
          <div className="pb-2">
            <ShareButton inputs={inputs} result={data ?? null} service="log-analytics" />
          </div>
          <ResourcesSection service="log-analytics" />
        </div>
      </div>

      {feedbackOpen && (
        <FeedbackModal
          service="log-analytics/ingestion"
          estimateValue={data ? `£${data.monthly_estimate.toFixed(2)}/mo` : 'N/A'}
          onClose={() => setFeedbackOpen(false)}
        />
      )}

      <MobileCostBar
        amount={data?.monthly_estimate}
        label="Log Analytics"
        targetId="la-results"
      />
    </div>
  );
}
